/**
 * Echo Hunt -- shared TypeScript types for the nested JSON shapes stored in the DB (see §14 of
 * `Docs/Plans/Others/2026-10-19_EchoHunt.md`).
 *
 * This module is PURE: no React, no `store`, no DB imports. It is imported by both the `@…` DB
 * record classes (which persist these shapes as JSON columns) and by the pure logic modules.
 *
 * Phases other than P2 add *logic* to these shapes; P2 only defines them so the tables can be
 * created and round-tripped.
 *
 * CONVENTION: each type is followed immediately by its `AddSchema` registration, so that renaming or
 * adding a field only ever requires editing one spot. The DB record classes reference these shapes
 * with `$ref`, which is why they must be registered at all (rather than being type-only).
 */
import {AddSchema} from "mobx-graphlink";
import {LLMRole_values, ReasoningEffort_values, RoleModelMap, RoleReasoningMap} from "./Roles.js";

// corpus
// ==========

/** Fixed enum of the event-types a bullet can describe (§4.1). Values are strings so they stay
 * readable in the DB and stable across reindexing. */
export const BulletEventType_values = [
	"powerUse",
	"perception",
	"social",
	"movement",
	"sceneChange",
	"interaction",
	"other",
] as const;
export type BulletEventType = typeof BulletEventType_values[number];
AddSchema("BulletEventType", {oneOf: BulletEventType_values.map(a=>({const: a}))});

/** Fixed enum of the reasons a bullet is excluded from the usable pool (§4.4). */
export const BulletFilterReason_values = [
	"thoughtOnly",
	"hedgeDominated",
	"metaCommentary",
	"wakingComparison",
	"lucidityMetaCognition",
	"bannedTerm",
	"tooShort",
	"noEvent",
	"empty",
	"styleViolation",
	/** P5: the index pass never produced a usable result for this bullet (the model omitted it, or
	 * every attempt failed). Not a property of the bullet's content, so it is re-tried on the next
	 * index run rather than being treated as a permanent verdict. */
	"indexFailed",
] as const;
export type BulletFilterReason = typeof BulletFilterReason_values[number];
AddSchema("BulletFilterReason", {oneOf: BulletFilterReason_values.map(a=>({const: a}))});

/** The filter reasons the INDEXER model may return itself (§4.4). `styleViolation` and
 * `indexFailed` are set by the deterministic post-pass, never by the model. */
export const BulletFilterReason_modelValues = BulletFilterReason_values.filter(a=>a != "styleViolation" && a != "indexFailed") as Exclude<BulletFilterReason, "styleViolation"|"indexFailed">[];

/** NOTE (v1, decided 2026-10-20): a bullet does NOT carry a lucid-phase/lucidity field. Judging which
 * moment of a dream was lucid is not reliable enough (the user often cannot tell either), and nothing
 * downstream consumed it -- it was only ever a supply-check dimension. The lucidity-related FILTER
 * (`lucidityMetaCognition`, §4.4) is unrelated and still applies: the character in the woven story
 * believes they are awake, so bullets describing realization/RCs/stabilizing are excluded outright. */

// rounds
// ==========

/** Whether a slot's text came from the user's journal or was generated (§5, §16.0).
 * IMPORTANT: this label must never reach the hunt UI before the reveal (§16.0 "label hygiene"). */
export const SlotSourceKind_values = ["real", "fake"] as const;
export type SlotSourceKind = typeof SlotSourceKind_values[number];
AddSchema("SlotSourceKind", {oneOf: SlotSourceKind_values.map(a=>({const: a}))});

/** Lifecycle of a round (§9.4, §16.2 P9). */
export const EchoRoundStatus_values = ["unjudged", "ok", "easy", "discarded"] as const;
export type EchoRoundStatus = typeof EchoRoundStatus_values[number];
AddSchema("EchoRoundStatus", {oneOf: EchoRoundStatus_values.map(a=>({const: a}))});

/** P10: one repair attempt applied to one slot, recorded so the judge-loop debug view can show
 * bullet-by-bullet what was tried and what came out of it (§9.3, §9.4).
 *
 * `textBefore`/`textAfter` are stored in full rather than as a diff: the history is a handful of short
 * sentences per round, and comparing two full texts by eye is what a person debugging a stubborn bullet
 * actually wants to do. `retentionOk == false` means the attempt was DISCARDED (the rewrite replaced the
 * event), so `textAfter` is the rejected text and `finalText` is unchanged. */
export type SlotRepairAttempt = {
	/** which repair-loop round this was (0-based, matching `JudgeAttempt.attemptIndex`) */
	attemptIndex: number;
	repair: JudgeRepair;
	/** how the repair was initiated: the automatic loop, or a manual retry from the debug view */
	source: "loop"|"manual";
	/** the text the slot carried going into this attempt */
	textBefore: string;
	/** the text the repair produced -- shown even when it was then REJECTED, so a failed attempt is visible */
	textAfter: string|null;
	/** false = the attempt was thrown away instead of applied. `reason` says why. */
	applied: boolean;
	/** human-readable outcome: retention failure, empty reply, generator exhaustion, etc. */
	reason: string;
	/** for a resample: which corpus row the new bullet came from (reals only) */
	newSourceId?: string|n;
	outputTokens?: number|n;
	createdAt: number;
};
AddSchema("SlotRepairAttempt", {
	properties: {
		attemptIndex: {type: "number"},
		repair: {$ref: "JudgeRepair"},
		source: {type: "string"},
		textBefore: {type: "string"},
		textAfter: {type: ["null", "string"]},
		applied: {type: "boolean"},
		reason: {type: "string"},
		newSourceId: {type: ["null", "string"]},
		outputTokens: {type: ["null", "number"]},
		createdAt: {type: "number"},
	},
});

/** One bullet's position in the woven story. `slotId` is assigned *after* shuffling and must be
 * random -- never derived from `sourceKind`/`sourceId` (§16.0). */
export type RoundSlot = {
	slotId: string;
	sourceKind: SlotSourceKind;
	/** id of the source `EchoCorpusBullet` (if real) or `GeneratedBullet` (if fake) */
	sourceId: string;
	/** seeded words used for the shared reshape step (§6); empty when the round is unseeded */
	reshapeWords: string[];
	/** text after the shared reshape step (§6) */
	reshapedText: string;
	/** text after the weaver rebridged it (§8.1) */
	wovenText: string;
	/** what the user actually saw (== `wovenText` unless a repair rewrite ran, §9.3) */
	finalText: string;
	/** did the reshaped text still describe the source event? (real bullets only, §6) */
	retentionOk: boolean;
	/** in-place repair rewrites applied to this slot (budget ~2, §9.3) */
	rewriteCount: number;
	/** how many times this slot's bullet was resampled during the judge loop (§9.3) */
	resampleCount: number;
	/** edit-distance between `reshapedText` and `wovenText`, logged for the §13.3 diagnostic */
	wovenEditDistance?: number|n;
	/** P10: the HELD-OUT judge's P(dream-sourced) for this slot, recorded for logging only (§9.5). It is
	 * never fed back into the loop -- a judge the loop can see is a judge the loop can overfit to. */
	heldoutScore?: number|n;
	/** P10: every repair attempt made on this slot, in order (§9.3). This is what makes a stubborn bullet
	 * diagnosable -- "the loop tried a rewrite, it failed retention, then it resampled" is visible here
	 * instead of being inferable only from the round-level counts. */
	repairHistory?: SlotRepairAttempt[]|n;
};
AddSchema("RoundSlot", {
	properties: {
		slotId: {type: "string"},
		sourceKind: {$ref: "SlotSourceKind"},
		sourceId: {type: "string"},
		reshapeWords: {items: {type: "string"}},
		reshapedText: {type: "string"},
		wovenText: {type: "string"},
		finalText: {type: "string"},
		retentionOk: {type: "boolean"},
		rewriteCount: {type: "number"},
		resampleCount: {type: "number"},
		wovenEditDistance: {type: ["null", "number"]},
		heldoutScore: {type: ["null", "number"]},
		repairHistory: {oneOf: [{type: "null"}, {items: {$ref: "SlotRepairAttempt"}}]},
	},
});

/** Recall kind the user reported when marking a slot (§10.3). */
export const RecallKind_values = ["remember", "know"] as const;
export type RecallKind = typeof RecallKind_values[number];
AddSchema("RecallKind", {oneOf: RecallKind_values.map(a=>({const: a}))});

/** One user mark for one slot (§10, §13.1). */
export type Answer = {
	slotId: string;
	/** did the user include this slot in their guess-list? */
	marked: boolean;
	/** 0 = definitely LLM-sourced, 100 = definitely dream-sourced (§10.1) */
	confidence: number;
	/** optional Remember/Know self-report (§10.3) */
	recallKind?: RecallKind|n;
	/** optional free-text, e.g. the RC "why couldn't this be waking life?" note (§10.2) */
	why?: string|n;
	/** ms from round-start to this mark (§10, §13.1) */
	msToMark: number;
	/** user believed this was LLM-sourced and wanted to say why (§10.3) */
	llmTellFlag?: boolean|n;
	/** the tell phrase the user supplied, feeding the cliché blacklist (§5.5, §10.3) */
	tellPhrase?: string|n;
};
AddSchema("Answer", {
	properties: {
		slotId: {type: "string"},
		marked: {type: "boolean"},
		confidence: {type: "number"},
		recallKind: {oneOf: [{type: "null"}, {$ref: "RecallKind"}]},
		why: {type: ["null", "string"]},
		msToMark: {type: "number"},
		llmTellFlag: {type: ["null", "boolean"]},
		tellPhrase: {type: ["null", "string"]},
	},
});

/** What repair the judge loop chose in response to a failed attempt (§9.3). */
export const JudgeRepair_values = ["resampleFake", "resampleReal", "rewriteReal", "none"] as const;
export type JudgeRepair = typeof JudgeRepair_values[number];
AddSchema("JudgeRepair", {oneOf: JudgeRepair_values.map(a=>({const: a}))});

/** One judge invocation in the repair loop (§9). */
export type JudgeAttempt = {
	/** which judge produced this (model id, or "heldOut") */
	judge: string;
	/** P(dream-sourced) per slot id */
	scores: Record<string, number>;
	/** AUC over `scores` against the true labels (§9.2) */
	auc?: number|n;
	/** top-X miscategorization %, logged alongside AUC (§9.2) */
	miscategorizationPct?: number|n;
	/** what repair was chosen in response, if any (§9.3). Recorded on the FIRST entry of an attempt,
	 * since the decision is made once per attempt rather than once per judge call. */
	repair?: JudgeRepair|n;
	/** token cost of this call, for the per-round counter (§13.1) */
	outputTokens?: number|n;
	/** P10: which repair-loop round this call belonged to (0-based). Lets the log be read round by round
	 * without guessing where one attempt's judges end and the next begins. */
	attemptIndex?: number|n;
	/** P10: did this judge land inside the two-sided AUC band (§9.2)? `false` is what triggers a repair. */
	inBand?: boolean|n;
	/** P10: the band this call was measured against, snapshotted so an old round stays interpretable after
	 * the setting changes (the same reason the round snapshots `xShare`/`yTarget`). */
	bandMin?: number|n;
	bandMax?: number|n;
};
AddSchema("JudgeAttempt", {
	properties: {
		judge: {type: "string"},
		scores: {type: "object"},
		auc: {type: ["null", "number"]},
		miscategorizationPct: {type: ["null", "number"]},
		repair: {oneOf: [{type: "null"}, {$ref: "JudgeRepair"}]},
		outputTokens: {type: ["null", "number"]},
		attemptIndex: {type: ["null", "number"]},
		inBand: {type: ["null", "boolean"]},
		bandMin: {type: ["null", "number"]},
		bandMax: {type: ["null", "number"]},
	},
});

/** P10: the outcome of one judge loop, stored on the round so the reveal can report how hard the round
 * actually was (§16.2 P10 acceptance: "a judge-loop summary shows on the reveal screen"). Everything here
 * is label-derived, so it lives on the round and is only SHOWN after the reveal. */
export type JudgeLoopSummary = {
	/** did the loop end inside the band (`ok`), or hit the hard stop (`easy`/`discarded`)? */
	landedInBand: boolean;
	/** repair-loop rounds run (1 = the first judgement already landed) */
	attempts: number;
	/** how many judge calls were made in total, across all rounds and models */
	judgeCalls: number;
	/** the accepted (or last) metric */
	auc: number|n;
	miscategorizationPct: number|n;
	/** the band the loop was measured against (§9.2) */
	bandMin: number;
	bandMax: number;
	/** repairs applied, by kind (§9.3) */
	repairs: Partial<Record<JudgeRepair, number>>;
	/** §9.4: the bullets that resisted hiding -- the most stranger-detectable kinds of dream content.
	 * Only real slots are listed: a fake the judge kept spotting says nothing about the user's dreams. */
	resistingSlotIds: string[];
	/** the held-out judge's AUC, for the §13.3 "personal margin" metric (§9.5) */
	heldOutAuc: number|n;
	/** tokens spent on judging (loop judges + held-out judge) */
	outputTokens: number;
};
AddSchema("JudgeLoopSummary", {
	properties: {
		landedInBand: {type: "boolean"},
		attempts: {type: "number"},
		judgeCalls: {type: "number"},
		auc: {type: ["null", "number"]},
		miscategorizationPct: {type: ["null", "number"]},
		bandMin: {type: "number"},
		bandMax: {type: "number"},
		repairs: {type: "object"},
		resistingSlotIds: {items: {type: "string"}},
		heldOutAuc: {type: ["null", "number"]},
		outputTokens: {type: "number"},
	},
});

/** Why a generated candidate was discarded (§5.2, §5.4). */
export const GeneratedBulletRejectReason_values = [
	"seedWordLeak",
	"tooTypical",
	"styleViolation",
	"retriesExhausted",
	"resampled",
] as const;
export type GeneratedBulletRejectReason = typeof GeneratedBulletRejectReason_values[number];
AddSchema("GeneratedBulletRejectReason", {oneOf: GeneratedBulletRejectReason_values.map(a=>({const: a}))});

/** A generated (never-journal) bullet, including rejected candidates (§5).
 * IMPORTANT: fakes live only inside the round record -- they must never reach the corpus
 * (invariant #5). */
export type GeneratedBullet = {
	id: string;
	model: string;
	/** the 2 words handed to the generator (§5.2) */
	seedWords: string[];
	/** the mid-level categories used instead of words, if the category variant is enabled (§5.2) */
	seedCategories?: string[]|n;
	eventType: BulletEventType;
	text: string;
	/** cosine similarity to the nearest corpus bullet; the typicality cap reads this (§5.4) */
	nearestCorpusSim?: number|n;
	/** why this candidate was thrown away, if it was (§5.2, §5.4) */
	rejected?: GeneratedBulletRejectReason|n;
};
AddSchema("GeneratedBullet", {
	properties: {
		id: {type: "string"},
		model: {type: "string"},
		seedWords: {items: {type: "string"}},
		seedCategories: {oneOf: [{type: "null"}, {items: {type: "string"}}]},
		eventType: {$ref: "BulletEventType"},
		text: {type: "string"},
		nearestCorpusSim: {type: ["null", "number"]},
		rejected: {oneOf: [{type: "null"}, {$ref: "GeneratedBulletRejectReason"}]},
	},
});

/** Per-role LLM model overrides (§16.1, P3). Absent/`null` means "fall back to the global model".
 * The role list itself lives in `Roles.ts` (shared by the pure LLM helper), and the schema below is
 * derived from it so adding a role only ever needs editing one spot. */
export type EchoHuntModelRoles = RoleModelMap;
AddSchema("EchoHuntModelRoles", {
	properties: Object.fromEntries(LLMRole_values.map(role=>[role, {type: ["null", "string"]}])),
});

/** Per-role reasoning-effort overrides (2026-10-21). Absent/`null` means "auto": the model's own metadata
 * decides, so a model that requires reasoning gets its default effort and every other model is left alone. */
export type EchoHuntModelReasoning = RoleReasoningMap;
AddSchema("EchoHuntModelReasoning", {
	properties: Object.fromEntries(LLMRole_values.map(role=>[role, {oneOf: [{type: "null"}, {$ref: "ReasoningEffort"}]}])),
});
AddSchema("ReasoningEffort", {oneOf: ReasoningEffort_values.map(a=>({const: a}))});

/** A door the weaver offered at the end of a story installment (§8.3, §11.5 W3).
 *
 * The TARGET is fixed when the door is described, not when it is used. That is what lets the weaver
 * describe the far side honestly, and it is what makes resolving the door a lookup rather than a fresh
 * random draw (which could contradict the description). */
export type EchoDoorOffer = {
	description: string;
	/** the world this door leads to, chosen at weave time. Optional because a pre-11.5 row has none, and
	 * because an offer can be salvaged from a bare string reply before a target is stamped on. */
	targetWorldId?: string|n;
};
AddSchema("EchoDoorOffer", {
	properties: {
		description: {type: "string"},
		targetWorldId: {type: ["null", "string"]},
	},
});

// stories
// ==========

/** A world the character can be dropped into (§8.5). */
export type EchoWorld = {
	id: string;
	name: string;
	/** short bible, <=300 words (§8.5) */
	bible: string;
	/** higher = more tolerant of wild/absurd events surviving un-smoothed (§8.5) */
	crazinessTolerance: number;
	/** true for LLM-generated original worlds rather than canonical ones (§8.5) */
	generated: boolean;
};
AddSchema("EchoWorld", {
	properties: {
		id: {type: "string"},
		name: {type: "string"},
		bible: {type: "string"},
		crazinessTolerance: {type: "number"},
		generated: {type: "boolean"},
	},
});

/** The character the woven story follows (§8.2). A PERSISTENT, user-authored entry in
 * `echoProfiles.characters` (§8.7) -- a story references it by id rather than owning a copy, so
 * editing the character changes the running story on purpose. */
export type EchoCharacter = {
	id: string;
	name: string;
	/** one line for the pool's list view, so the editor does not have to show a wall of notes (§8.7) */
	tagline: string;
	/** -1 = cautious, +1 = opportunistic (§8.2, §8.5) */
	values_cautionVsOpportunity: number;
	/** a few free-form personality notes the weaver should honor (§8.5) */
	personalityNotes: string;
	/** true when a field (or the whole entry) was drafted by the autogen path rather than typed by the
	 * user (§8.7). Purely informational: nothing in the pipeline behaves differently. */
	generated: boolean;
};
AddSchema("EchoCharacter", {
	properties: {
		id: {type: "string"},
		name: {type: "string"},
		tagline: {type: "string"},
		values_cautionVsOpportunity: {type: "number"},
		personalityNotes: {type: "string"},
		generated: {type: "boolean"},
	},
});

/** One world the story visited, in order (§14 `echoStories` world-visit history).
 *
 * THE HISTORY IS THE SINGLE SOURCE OF TRUTH for where a story is (§11.5 W1): `story.worldId` was
 * removed. Every world change is exactly one append -- a door the character took, a manual move by the
 * user, or a recovery after the world was deleted from the pool. */
export type WorldVisit = {
	worldId: string;
	/** round index at which this world was entered */
	startedAtRoundIndex: number;
	/** P11: the world's name at the time it was entered. A visit outlives the world pool entry -- the user
	 * may delete or rename a world mid-story -- and a history that could not say where the character went
	 * would be unreadable exactly when it matters. Optional, so a pre-P11 row still parses. */
	worldName?: string|n;
	/** §11.5 W4: the world the character came FROM, captured at the time of the move. Lets the weaver be
	 * told about the transition without having to walk the history itself. */
	fromWorldId?: string|n;
	fromWorldName?: string|n;
	/** §11.5 W4: why the story moved. `door` = the character went through one; `manual` = the user moved it
	 * on the Story tab; `recovery` = the previous world was deleted from the pool. */
	cause?: WorldChangeCause|n;
	/** the door's description, when `cause` is `door` -- the arrival block quotes it back to the weaver so
	 * the new installment can open with the same image the player was shown. */
	doorDescription?: string|n;
};
AddSchema("WorldVisit", {
	properties: {
		worldId: {type: "string"},
		startedAtRoundIndex: {type: "number"},
		worldName: {type: ["null", "string"]},
		fromWorldId: {type: ["null", "string"]},
		fromWorldName: {type: ["null", "string"]},
		cause: {oneOf: [{type: "null"}, {$ref: "WorldChangeCause"}]},
		doorDescription: {type: ["null", "string"]},
	},
});

/** §11.5 W4: why a story moved between worlds. */
export const WorldChangeCause_values = ["door", "manual", "recovery"] as const;
export type WorldChangeCause = typeof WorldChangeCause_values[number];
AddSchema("WorldChangeCause", {oneOf: WorldChangeCause_values.map(a=>({const: a}))});

/** §11.5 W2: the PLAYER's verdict on a door the weaver offered.
 *
 * This is an AUTHORIAL decision taken outside the fiction: the character has not seen the door yet. The
 * player decides whether this possibility should exist in the story at all. The character's own decision
 * to go through it comes LATER, in the next round (`DoorDecision`), which is what keeps their agency. */
export const DoorVerdict_values = ["accept", "decline"] as const;
export type DoorVerdict = typeof DoorVerdict_values[number];
AddSchema("DoorVerdict", {oneOf: DoorVerdict_values.map(a=>({const: a}))});

/** §11.5 W2: the CHARACTER's decision, made in the fiction when a staged door is present.
 * Only `taken` moves the story. */
export const DoorDecision_values = ["taken", "declined"] as const;
export type DoorDecision = typeof DoorDecision_values[number];
AddSchema("DoorDecision", {oneOf: DoorDecision_values.map(a=>({const: a}))});

/** P11 (§8.4): the suspicion meter's knobs.
 *
 * The meter is UNCONDITIONAL -- `suspicion` keeps accumulating on the story whatever these say, so the
 * number is always an honest record of how often the user has found their own echoes. What `applied`
 * gates is whether that number is ACTED ON: with it off, the story is never interrupted to become lucid
 * and never ends because of suspicion, so the character keeps going and the meter is just a readout.
 * That split is deliberate: turning the feature off should not also erase the history, and turning it
 * back on should not need the missing rounds to be replayed. */
export type EchoSuspicionSettings = {
	/** act on the meter at all: past the threshold, narrate the lucid interlude and close the story (§8.4).
	 * OFF still records suspicion -- it simply never changes the story. */
	applied: boolean;
	/** 0..1. Reaching this raises the character's realisation and ends the run. 0 would trigger on every
	 * round, so the UI warns rather than silently clamping it. */
	threshold: number;
	/** suspicion added per correct echo find (§8.4 "your correct echo finds raise the character's suspicion") */
	gainPerHit: number;
	/** suspicion removed per missed real bullet. Small on purpose: a miss is a failure of attention, not
	 * evidence that the character is awake. */
	lossPerMiss: number;
};
AddSchema("EchoSuspicionSettings", {
	properties: {
		applied: {type: "boolean"},
		threshold: {type: "number"},
		gainPerHit: {type: "number"},
		lossPerMiss: {type: "number"},
	},
});

// profiles
// ==========

/** Where a cliché-blacklist entry came from (§5.5, §13.3). */
export const ClicheBlacklistEntrySource_values = ["manual", "tellFlag"] as const;
export type ClicheBlacklistEntrySource = typeof ClicheBlacklistEntrySource_values[number];
AddSchema("ClicheBlacklistEntrySource", {oneOf: ClicheBlacklistEntrySource_values.map(a=>({const: a}))});

/** A user-confirmed phrase that fakes must not use (§5.5, §13.3 blacklist growth). */
export type ClicheBlacklistEntry = {
	phrase: string;
	/** where it came from: hand-authored, or promoted from a reveal-time tell phrase */
	source: ClicheBlacklistEntrySource;
	/** how many times `CheckBullet` has matched it (P13) */
	hits: number;
};
AddSchema("ClicheBlacklistEntry", {
	properties: {
		phrase: {type: "string"},
		source: {$ref: "ClicheBlacklistEntrySource"},
		hits: {type: "number"},
	},
});

/** The user's seed-word settings (§5.2, §6): which words may be seeds, and how many per fake.
 * Defaults reproduce the cutoffs the manual tags were built with (see `Wordlist.ts`), including the
 * 4.48 concreteness floor that tagging covers -- the Settings tab warns loudly below it. */
export type EchoWordlistSettings = {
	/** minimum `Conc.M` (mean concreteness rating, 1..5) */
	concretenessMin: number;
	/** minimum `Percent_known` (share of raters who knew the word, 0..1) */
	percentKnownMin: number;
	/** manual tags whose presence excludes a word (§5.2: cheap to visualize, cheap to integrate, safe) */
	excludedTags: string[];
	/** merge `swing set`/`swingset`/`swing-set` style variants into one entry */
	mergeDuplicates: boolean;
	/** How many seed CONCEPTS one fake is built around, drawn uniformly from `[min, max]` inclusive.
	 * A deliberate experiment knob: the original design drew this from the corpus `oddityCount`
	 * distribution, which clusters at 0-1 and so pinned every fake to one concept. */
	seedConceptCountMin: number;
	seedConceptCountMax: number;
};
AddSchema("EchoWordlistSettings", {
	properties: {
		concretenessMin: {type: "number"},
		percentKnownMin: {type: "number"},
		excludedTags: {items: {type: "string"}},
		mergeDuplicates: {type: "boolean"},
		seedConceptCountMin: {type: "number"},
		seedConceptCountMax: {type: "number"},
	},
});

/** How strongly reshaping/reskinning rewrites bullets (§11). */
export const RewriteStrength_values = ["light", "medium", "heavy"] as const;
export type RewriteStrength = typeof RewriteStrength_values[number];
AddSchema("RewriteStrength", {oneOf: RewriteStrength_values.map(a=>({const: a}))});

/** Which single setting the auto-adjuster is currently tuning (§11). */
export const DifficultyAdjustTarget_values = [
	"yTarget",
	"responseDeadlineMS",
	"rewriteStrength",
	"fakeTypicalityThreshold",
	"sourceAge",
] as const;
export type DifficultyAdjustTarget = typeof DifficultyAdjustTarget_values[number];
AddSchema("DifficultyAdjustTarget", {oneOf: DifficultyAdjustTarget_values.map(a=>({const: a}))});

/** Difficulty state, auto-adjusted one setting at a time, upward only (§11). */
export type EchoDifficultyState = {
	/** minimum miscategorization the judge must reach (§9.1) */
	yTarget: number;
	/** response deadline in ms per bullet; null = untimed (§11) */
	responseDeadlineMS: number|n;
	/** how strongly reshaping/reskinning rewrites bullets (§11) */
	rewriteStrength: RewriteStrength;
	/** how close fakes may sit to the corpus before the typicality cap rejects them (§5.4) */
	fakeTypicalityThreshold: number;
	/** which single setting is currently being adjusted (§11); null = not adjusting */
	adjusting: DifficultyAdjustTarget|n;
};
AddSchema("EchoDifficultyState", {
	properties: {
		yTarget: {type: "number"},
		responseDeadlineMS: {type: ["null", "number"]},
		rewriteStrength: {$ref: "RewriteStrength"},
		fakeTypicalityThreshold: {type: "number"},
		adjusting: {oneOf: [{type: "null"}, {$ref: "DifficultyAdjustTarget"}]},
	},
});

/** P10: the judge loop's knobs (§9.1, §9.2, §9.3). Stored on the profile so a round can snapshot them. */
export type EchoJudgeSettings = {
	/** Lower edge of the two-sided AUC band (§9.2). A judge BELOW this is confidently inverted, which is
	 * a tell in the other direction, so the band has a floor as well as a ceiling. */
	bandMin: number;
	/** Upper edge of the band (§9.2). Above it, the judge is genuinely seeing something and the round is
	 * too easy. */
	bandMax: number;
	/** Judge models used by the LOOP, one call each per round (§9.2 "2 judge calls or 2 model families").
	 * Empty = the `judge` role's configured model, called twice. */
	judgeModels: string[];
	/** Repair-loop rounds before the hard stop (§9.1, §9.4). */
	maxAttempts: number;
	/** In-place repair rewrites allowed per slot (§9.3). After this, the slot is resampled instead. */
	rewriteBudgetPerSlot: number;
	/** Rejection-sample each fake against the held-out judge and keep the most human one (§5.5).
	 * Default OFF: it costs several extra paid calls per fake for a benefit nobody has measured yet. */
	rejectionSampling: boolean;
	/** How many candidates rejection sampling draws, when enabled. */
	rejectionSamplingCandidates: number;
};
AddSchema("EchoJudgeSettings", {
	properties: {
		bandMin: {type: "number"},
		bandMax: {type: "number"},
		judgeModels: {items: {type: "string"}},
		maxAttempts: {type: "number"},
		rewriteBudgetPerSlot: {type: "number"},
		rejectionSampling: {type: "boolean"},
		rejectionSamplingCandidates: {type: "number"},
	},
});
